import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import { DAYS_OF_WEEK } from '../utils/schedule';

export default function RoomScheduleEditor({ schedule, onChange }) {
  const handleAlwaysOnChange = (event) => {
    const { checked } = event.target;
    onChange((prev) => ({
      ...prev,
      alwaysOn: checked,
    }));
  };

  const handleDayToggle = (dayKey) => (event) => {
    const { checked } = event.target;
    onChange((prev) => ({
      ...prev,
      days: {
        ...prev.days,
        [dayKey]: {
          ...prev.days[dayKey],
          enabled: checked,
        },
      },
    }));
  };

  const handleTimeChange = (dayKey, field) => (event) => {
    const value = event.target.value;
    onChange((prev) => ({
      ...prev,
      days: {
        ...prev.days,
        [dayKey]: {
          ...prev.days[dayKey],
          [field]: value,
        },
      },
    }));
  };

  const disableDays = schedule.alwaysOn;

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6">Listening schedule</Typography>
      <FormControlLabel
        sx={{ mt: 1 }}
        control={<Checkbox checked={schedule.alwaysOn} onChange={handleAlwaysOnChange} />}
        label="Keep devices active 24/7"
      />
      {schedule.alwaysOn && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
          Daily windows are disabled while always-on mode is active.
        </Typography>
      )}
      <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
        Configure the daily listening window for this room. Devices outside the active window stay idle.
      </Typography>
      <Divider sx={{ my: 2 }} />
      {DAYS_OF_WEEK.map((day, index) => {
        const dayConfig = schedule.days[day.key];
        const timeDisabled = disableDays || !dayConfig.enabled;
        const lastItem = index === DAYS_OF_WEEK.length - 1;
        return (
          <Box
            key={day.key}
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 2,
              py: 1.5,
              borderBottom: lastItem ? 'none' : '1px solid',
              borderColor: 'divider',
            }}
          >
            <FormControlLabel
              control={(
                <Checkbox
                  checked={dayConfig.enabled}
                  onChange={handleDayToggle(day.key)}
                  disabled={disableDays}
                />
              )}
              label={day.label}
            />
            <TextField
              type="time"
              label="Start"
              value={dayConfig.start}
              onChange={handleTimeChange(day.key, 'start')}
              disabled={timeDisabled}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
              sx={{ minWidth: 140 }}
            />
            <TextField
              type="time"
              label="End"
              value={dayConfig.end}
              onChange={handleTimeChange(day.key, 'end')}
              disabled={timeDisabled}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
              sx={{ minWidth: 140 }}
            />
            {!dayConfig.enabled && !disableDays && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Devices stay idle this day
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
